import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  Brain, 
  History, 
  Calendar,
  BellRing, 
  Wand2,
  ChevronRight,
  PanelLeft,
  ChevronLeft,
  RefreshCw,
  Settings,
  Coins,
  LayoutDashboard,
  MessageSquareText
} from "lucide-react";
import { cn } from "@/lib/utils";
import "./sidebar-styles.css";
import { Image } from "@/components/ui/image";
import { useIsMobile } from "@/hooks/use-mobile";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const SIDEBAR_WIDTH_EXPANDED = "220px";
const SIDEBAR_WIDTH_COLLAPSED = "56px";
const SIDEBAR_SHORTCUT_KEY = "b";

export const SimpleNavSidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Get collapsed state from localStorage or default to true
    const stored = localStorage.getItem("sidebar-collapsed");
    return stored !== null ? stored === "true" : true;
  });
  
  // User profile state
  const [userInitials, setUserInitials] = useState("?");
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");

  // Save collapsed state to localStorage and dispatch custom event when it changes
  useEffect(() => {
    localStorage.setItem("sidebar-collapsed", isCollapsed.toString());
    // Dispatch custom event for other components to listen for
    window.dispatchEvent(new CustomEvent('sidebar-toggle', { detail: { isCollapsed } }));
  }, [isCollapsed]);

  // Add keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.altKey || event.metaKey) && event.key === SIDEBAR_SHORTCUT_KEY) {
        event.preventDefault();
        setIsCollapsed(prev => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);
  
  // Get user profile on component mount
  useEffect(() => {
    async function getUserProfile() {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUserEmail(user.email || "");
        // Create initials from email
        if (user.email) {
          const email = user.email;
          const parts = email.split('@')[0].split('.');
          const initials = parts.map(part => part[0].toUpperCase()).join('');
          setUserInitials(initials.substring(0, 2));
          setUserName(parts.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' '));
        }
      }
    }
    
    getUserProfile();
  }, []);

  const isActive = (path: string) => {
    if (path === "/dashboard" && (location.pathname === "/" || location.pathname === "/dashboard")) {
      return true;
    }
    return location.pathname === path;
  };

  // Function to navigate to profile
  const goToProfile = () => {
    navigate('/profile');
  };

  // Function to reset tutorials
  const resetTutorials = () => {
    // Remove tutorial completion flag
    localStorage.removeItem('strykr-tutorial-completed');
    
    // Find and remove all feature tooltip flags
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('strykr-tooltip-')) {
        keysToRemove.push(key);
      }
    }
    
    // Remove all identified keys
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    toast({
      title: "Onboarding Guide Activated",
      description: "Tutorial will be shown again."
    });
    
    // Reload the page to show the tutorial
    setTimeout(() => window.location.reload(), 1500);
  };
  
  // Navigation items
  const navItems = [
    {
      path: "/dashboard",
      label: "Strykr Dashboard",
      icon: <LayoutDashboard />,
      colorActive: "bg-gradient-to-bl from-blue-400 via-blue-500 to-blue-600 shadow-lg shadow-blue-500/50 border border-white/10 backdrop-blur-sm",
      colorInactive: "bg-gradient-to-br from-blue-500/20 to-blue-500/30 hover:from-blue-500/30 hover:to-blue-500/40 backdrop-blur-sm"
    },
    {
      path: "/chat",
      label: "Ask Strykr",
      icon: <MessageSquareText />,
      colorActive: "bg-gradient-to-bl from-indigo-400 via-purple-500 to-pink-500 shadow-lg shadow-purple-500/50 border border-white/10 backdrop-blur-sm",
      colorInactive: "bg-gradient-to-br from-indigo-500/20 to-purple-500/30 hover:from-indigo-500/30 hover:to-purple-500/40 backdrop-blur-sm"
    },
    {
      path: "/calendar",
      label: "Economic Calendar",
      icon: <Calendar />,
      colorActive: "bg-gradient-to-bl from-amber-400 via-orange-500 to-red-600 shadow-lg shadow-orange-500/50 border border-white/10 backdrop-blur-sm",
      colorInactive: "bg-gradient-to-br from-amber-500/20 to-orange-500/30 hover:from-amber-500/30 hover:to-orange-500/40 backdrop-blur-sm"
    },
    {
      path: "/alerts",
      label: "Alpha Alerts",
      icon: <BellRing />,
      colorActive: "bg-gradient-to-bl from-red-400 via-rose-500 to-pink-600 shadow-lg shadow-rose-500/50 border border-white/10 backdrop-blur-sm",
      colorInactive: "bg-gradient-to-br from-red-500/20 to-rose-500/30 hover:from-red-500/30 hover:to-rose-500/40 backdrop-blur-sm"
    },
    {
      path: "/wallet-analyzer",
      label: "Wallet Analyzer",
      icon: <Coins />,
      colorActive: "bg-gradient-to-bl from-green-400 via-emerald-500 to-teal-600 shadow-lg shadow-emerald-500/50 border border-white/10 backdrop-blur-sm",
      colorInactive: "bg-gradient-to-br from-emerald-500/20 to-teal-500/30 hover:from-emerald-500/30 hover:to-teal-500/40 backdrop-blur-sm"
    },
    {
      path: "/market-screener",
      label: "Market Picks",
      icon: <Wand2 />,
      colorActive: "bg-gradient-to-bl from-green-400 via-emerald-500 to-teal-600 shadow-lg shadow-emerald-500/50 border border-white/10 backdrop-blur-sm",
      colorInactive: "bg-gradient-to-br from-emerald-500/20 to-teal-500/30 hover:from-emerald-500/30 hover:to-teal-500/40 backdrop-blur-sm"
    }
  ];

  // Render only the icon if sidebar is collapsed
  if (isMobile) return null;
  
  return (
    <div 
      className="fixed top-0 left-0 h-screen flex flex-col border-r border-indigo-700/20 bg-gradient-to-b from-[#0a0e18] to-[#0c1221] transition-all duration-150 ease-in-out hidden md:flex z-40 shadow-xl shadow-black/10"
      style={{ width: isCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED }}
    >
      {/* Header with logo and toggle button */}
      <div className={cn(
        "p-3.5 flex items-center backdrop-blur-sm border-b border-white/5",
        isCollapsed ? "justify-center flex-col gap-2" : "justify-between"
      )}>
        <div className={cn(
          "flex items-center", 
          isCollapsed ? "justify-center" : "gap-2"
        )}>
          <button
            onClick={() => navigate("/dashboard")}
            className={cn(
              "flex items-center transition-all duration-200 hover:bg-slate-800/20 rounded-lg p-1", 
              isCollapsed ? "justify-center" : "gap-2"
            )}
            title="Go to Dashboard"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full overflow-hidden bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg shadow-blue-900/30 border border-white/20 group-hover:shadow-blue-500/40 transition-shadow">
              <Image 
                src="/strykr-logo.jpeg"
                alt="Strykr AI" 
                className="w-7 h-7" 
              />
            </div>
            {!isCollapsed && (
              <div className="flex flex-col ml-1">
                <h2 className="text-sm font-medium tracking-wide text-white/95 group-hover:text-blue-200 transition-colors">Strykr AI</h2>
              </div>
            )}
          </button>
        </div>
        
        {!isCollapsed && (
          <button 
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="flex items-center justify-center h-6 w-6 rounded-md p-1 text-white/60 hover:bg-slate-800/40 hover:text-white transition-all duration-150"
            title="Collapse sidebar (Alt+B)"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
        )}
        
        {isCollapsed && (
          <button 
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="flex items-center justify-center h-6 w-6 rounded-md p-1 text-white/60 hover:bg-slate-800/40 hover:text-white transition-all duration-150"
            title="Expand sidebar (Alt+B)"
          >
            <PanelLeft className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      
      {/* Navigation label - modern styling */}
      {!isCollapsed && (
        <div className="px-3 pt-4 pb-1">
          <p className="text-[11px] font-medium uppercase tracking-wider text-indigo-300/50 text-left">Menu</p>
        </div>
      )}
      
      {/* Navigation items */}
      <div className="flex-1 flex flex-col gap-1.5 overflow-y-auto thin-scrollbar px-2 py-4">

        {navItems.map((item) => (
          <button
            key={item.path}
            onClick={() => navigate(item.path)}
            className={cn(
              "flex items-center w-full rounded-lg transition-all duration-150",
              isCollapsed ? "justify-center p-1.5" : "justify-between px-2 py-2",
              isActive(item.path) ? "bg-slate-800/20" : "hover:bg-slate-800/10"
            )}
          >
            <div className="flex items-center gap-3 w-full">
              <div 
                className={cn(
                  "flex items-center justify-center rounded-md p-1.5 transition-all duration-300 flex-shrink-0",
                  isActive(item.path) ? item.colorActive : item.colorInactive,
                  isCollapsed ? "w-8 h-8" : "w-7 h-7"
                )}
              >
                {React.cloneElement(item.icon as React.ReactElement, { 
                  className: cn(
                    "h-4 w-4", 
                    isActive(item.path) 
                      ? "text-white drop-shadow-glow sidebar-icon-active" 
                      : "text-white/90"
                  ) 
                })}
              </div>
              
              {!isCollapsed && (
                <span className={cn(
                  "text-[13px] font-medium text-left",
                  isActive(item.path) ? "text-white" : "text-white/70"
                )}>
                  {item.label}
                </span>
              )}
            </div>
            
            {!isCollapsed && isActive(item.path) && (
              <ChevronRight className="h-4 w-4 text-white/70 ml-auto" />
            )}
          </button>
        ))}
      </div>
      
      {/* Footer */}
      <div className="mt-auto border-t border-indigo-500/10">
        {isCollapsed ? (
          <div className="p-1.5 flex flex-col gap-1.5 items-center">
            <button 
              onClick={goToProfile}
              className="group relative p-1 rounded-full hover:shadow-md hover:shadow-indigo-900/30 transition-all duration-200"
            >
              <Avatar className="h-7 w-7 border border-indigo-500/30 group-hover:border-violet-500/40 transition-colors">
                <AvatarFallback className="bg-gradient-to-br from-blue-900 to-indigo-900">{userInitials}</AvatarFallback>
              </Avatar>
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
            </button>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button 
                    onClick={resetTutorials}
                    className="flex items-center justify-center h-7 w-7 rounded-full hover:bg-indigo-900/20 text-white/70 hover:text-violet-300 transition-colors"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p className="text-xs">Show tutorial</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <div className="text-[10px] text-white/40 mt-1">v3.0.0</div>
          </div>
        ) : (
          <div className="p-2">
            <button
              onClick={goToProfile}
              className="w-full mb-3 group hover:bg-indigo-900/10 p-1 rounded-lg transition-colors"
            >
              <div className="flex items-center gap-3">
                <Avatar className="h-7 w-7 border border-indigo-500/30 group-hover:border-violet-500/40 transition-colors">
                  <AvatarFallback className="bg-gradient-to-br from-blue-900 to-indigo-900">{userInitials}</AvatarFallback>
                </Avatar>
                <div className="flex flex-col flex-1 min-w-0 text-left">
                  <p className="text-sm font-medium text-white truncate">{userName}</p>
                  <p className="text-xs text-indigo-300/70 truncate flex items-center gap-1">
                    My Profile
                    <ChevronRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity text-violet-400" />
                  </p>
                </div>
              </div>
            </button>
            
            <div className="flex justify-between items-center">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button 
                      onClick={resetTutorials}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg hover:bg-indigo-900/20 text-white/70 hover:text-violet-300 transition-colors"
                    >
                      <RefreshCw className="h-4 w-4" />
                      <span className="text-xs font-medium">Show tutorial</span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p className="text-xs">Show onboarding guide</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <div className="p-2 text-xs text-white/50">v3.0.0</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
